"use client";

import { useState } from "react";
import { MessageCircle, Phone, X } from "lucide-react";

interface WhatsAppChatProps {
  phoneNumber: string;
  message?: string;
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  showTooltip?: boolean;
}

export default function WhatsAppChat({
  phoneNumber,
  position = "bottom-right",
  showTooltip = true,
}: WhatsAppChatProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  // @ts-ignore
  const [isVisible, setIsVisible] = useState(true);

  const formatPhoneNumber = (phone: string) => {
    // Remove any non-digit characters except +
    const cleaned = phone.replace(/[^\d+]/g, "");
    // Format for display (assuming international format)
    if (cleaned.startsWith("+")) {
      return cleaned.replace(/(\+\d{1,3})(\d{3})(\d{3})(\d{4})/, "$1 $2 $3 $4");
    }
    return cleaned;
  };

  const handleWhatsAppClick = () => {
    const cleanPhone = phoneNumber.replace(/[^\d+]/g, "");
    const encodedMessage = encodeURIComponent("");
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank");
  };

  const getPositionClasses = () => {
    switch (position) {
      case "bottom-left":
        return "bottom-6 left-6";
      case "top-right":
        return "top-6 right-6";
      case "top-left":
        return "top-6 left-6";
      default:
        return "bottom-6 right-6";
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed ${getPositionClasses()} z-50 flex flex-col items-end space-y-3`}
    >
      {/* Tooltip/Expanded Info */}
      {isExpanded && (
        <div className="animate-in slide-in-from-bottom-2 duration-300 bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 max-w-xs">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 text-sm">
                  WhatsApp Chat
                </h3>
                <p className="text-xs text-gray-500">We're online now</p>
              </div>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Phone className="w-4 h-4" />
              <span>{formatPhoneNumber(phoneNumber)}</span>
            </div>
            <p className="text-xs text-gray-500">
              Click to start a conversation on WhatsApp
            </p>
          </div>

          <button
            onClick={handleWhatsAppClick}
            className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium py-2.5 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            Start Chat
          </button>
        </div>
      )}

      {/* Main Floating Button */}
      <div className="relative">
        {/* Tooltip for collapsed state */}
        {showTooltip && !isExpanded && (
          <div className="absolute right-16 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white text-xs py-2 px-3 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            Chat with us on WhatsApp
            <div className="absolute left-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-l-gray-800"></div>
          </div>
        )}

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="group relative w-14 h-14 bg-gradient-to-br from-green-400 via-green-500 to-green-600 hover:from-green-500 hover:via-green-600 hover:to-green-700 rounded-full shadow-2xl hover:shadow-green-500/25 transition-all duration-300 transform hover:scale-110 animate-bounce-slow"
          style={{
            animation: "float 3s ease-in-out infinite",
          }}
        >
          {/* Ripple Effect */}
          <div className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-20"></div>

          {/* Icon */}
          <div className="relative z-10 flex items-center justify-center w-full h-full">
            <MessageCircle className="w-7 h-7 text-white transform group-hover:scale-110 transition-transform duration-200" />
          </div>

          {/* Online Indicator */}
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white">
            <div className="w-full h-full bg-green-400 rounded-full animate-pulse"></div>
          </div>
        </button>

        {/* Close button when expanded */}
        {/* {isExpanded && ( */}
        {/*   <button */}
        {/*     onClick={() => setIsVisible(false)} */}
        {/*     className="absolute -top-2 -left-2 w-6 h-6 bg-gray-600 hover:bg-gray-700 text-white rounded-full flex items-center justify-center transition-colors duration-200 text-xs" */}
        {/*   > */}
        {/*     <X className="w-3 h-3" /> */}
        {/*   </button> */}
        {/* )} */}
      </div>

      <style>{`
				@keyframes float {
					0%,
					100% {
						transform: translateY(0px);
					}
					50% {
						transform: translateY(-10px);
					}
				}

				.animate-bounce-slow {
					animation: bounce 2s infinite;
				}

				@keyframes bounce {
					0%,
					100% {
						transform: translateY(-25%);
						animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
					}
					50% {
						transform: translateY(0);
						animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
					}
				}
			`}</style>
    </div>
  );
}
